<template>
  <div class="event-form" v-if="newEvent">
    <section>
      <div class="event-form--section-title">
        <span>Date and Time</span>
      </div>

      <div v-if="overDays" class="booking-error input-error-text">
        You have more days than your booking allows.
        <template v-if="newEvent.booking">(max {{ newEvent.booking.days }})</template>
      </div>
      <div v-if="wrongDays" class="booking-error input-error-text">
        Your start or end date is different from your booking.
        <template v-if="newEvent.booking">
          ({{ dayjs(newEvent.booking.start_day).format('MM/DD/YYYY') }} -
          {{ dayjs(newEvent.booking.end_day).format('MM/DD/YYYY') }})
        </template>
      </div>

      <EventDateTimeNew
        v-if="canChangeStart && !pastEvent"
        v-model:newEvent="newEvent"
        v-model:hasDateOverrides="hasDateOverrides"
        v-model:dateIncludes="dateIncludes"
      />

      <EventDateTimeStarted
        v-if="!canChangeStart && !pastEvent"
        v-model:newEvent="newEvent"
        v-model:hasDateOverrides="hasDateOverrides"
        v-model:dateIncludes="dateIncludes"
      />

      <div v-if="pastEvent" class="event-form--content">
        <p>You cannot change dates after the event has ended.</p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import dayjs from 'dayjs';

import EventDateTimeNew from './EventDateTimeNew.vue';
import EventDateTimeStarted from './EventDateTimeStarted.vue';

import type { EventTypeNew } from '@/types/Event';

const newEvent = defineModel<EventTypeNew>('newEvent');
const hasDateOverrides = defineModel<boolean>('hasDateOverrides');
const dateIncludes = defineModel<string[]>('dateIncludes');

const props = defineProps<{
  isEdit: boolean;
}>();

const showExceptions = ref<boolean>(false);

const eventDates = ref<Date[]>();
const eventDatesExtra = ref<Date[]>([]);
const eventFrom = ref<Date>(dayjs().hour(9).minute(0).toDate());
const eventTo = ref<Date>(dayjs().hour(17).minute(0).toDate());

if (hasDateOverrides.value === true) {
  showExceptions.value = true;
}

onMounted(() => {
  if (newEvent.value?.event_start && newEvent.value?.event_end) {
    eventDates.value = [
      dayjs(newEvent.value.event_start).toDate(),
      dayjs(newEvent.value.event_end).toDate(),
    ];
    eventFrom.value = dayjs(newEvent.value.event_start).toDate();
    eventTo.value = dayjs(newEvent.value.event_end).toDate();
  }

  if (props.isEdit) {
    newEvent.value?.dates.forEach((date) => {
      if (dayjs(date.from).isAfter(dayjs())) {
        eventDatesExtra.value?.push(date.from);
      }
    });
  }
});

const canChangeStart = computed(() => {
  if (!props.isEdit) {
    return true;
  }
  return dayjs(eventFrom.value).isAfter(dayjs());
});

const pastEvent = computed(() => {
  if (!props.isEdit) {
    return false;
  }
  return dayjs(eventTo.value).isBefore(dayjs().add(1, 'day'));
});

const overDays = computed(() => {
  if (!newEvent.value?.booking) {
    return false;
  }

  if (!newEvent.value.booking.days) {
    return false;
  }

  const totalDays =
    dayjs(newEvent.value?.event_end)
      .add(1, 'day')
      .diff(dayjs(newEvent.value?.event_start), 'day') || 0;

  return newEvent.value.booking.days < totalDays;
});

const wrongDays = computed(() => {
  if (!newEvent.value?.booking) {
    return false;
  }

  if (!newEvent.value.booking.start_day || !newEvent.value.booking.end_day) {
    return false;
  }

  const sameStart =
    dayjs(newEvent.value.booking.start_day).format('YYYY-MM-DD') ===
    dayjs(newEvent.value.event_start).format('YYYY-MM-DD');
  const sameEnd =
    dayjs(newEvent.value.booking.end_day).format('YYYY-MM-DD') ===
    dayjs(newEvent.value.event_end).format('YYYY-MM-DD');

  return !sameStart || !sameEnd;
});
</script>

<style lang="scss">
.exceptions-section {
  margin-top: variables.$vantage-base-sizing-xs;

  .event-form--section-title {
    border-bottom: 0;
    padding-left: 0;
  }
}

.event-form--row-flex {
  > .p-floatlabel {
    flex-basis: 33%;
  }

  .p-datepicker-input {
    min-width: auto;
  }
}

.event-form--content {
  position: relative;

  p {
    margin-bottom: variables.$vantage-base-sizing-lg;
  }
}

.form-row--event-datetime {
  .form-cell {
    flex-basis: calc(25% - 16px);
    width: calc(25% - 16px);
  }

  .form-cell:first-of-type {
    flex-basis: calc(50% - 16px);
    width: calc(50% - 16px);
  }

  .p-datepicker.p-component {
    width: 100%;
  }

  .p-datepicker-input {
    min-width: auto;
  }
}

.booking-error {
  padding: variables.$vantage-base-sizing-lg variables.$vantage-base-sizing-lg 0
    variables.$vantage-base-sizing-lg;
}
</style>
